import { ApplicationService } from 'app/services/application.service/application.service';
import { EventHandler } from 'app/classes';
import { IdentityService, LodeRunnerService } from 'app/services';
import { Company } from '../company/company';
import { Singleton, Url } from 'app/utils';
import { CurrentUserProfile } from './current.user.profile';
import * as prefHelpers from 'app/utils/prefHelpers/prefHelpers';
import * as accessibility from 'app/utils/accessibility/accessibility';
import { SETTINGS } from 'app/settings';

import * as Cookie from 'browser-cookies';
import { $Router } from '../router/router';

const TOKEN_COOKIE = 'token';
const LANGUAGE_COOKIE = 'language';

export interface IUserPreferences {
    locale: string;
    timezone: string;
    language: (Array<string> | string);
    datetime: any;
}

export class CurrentUser {

    public id: string;
    public apps: Array<any>;
    public type: string;
    public token: string;
    public title: string;
    public email: string;
    public roles: Array<string>;
    public profile: CurrentUserProfile;
    public session: Promise<string>;
    public sessionExpires?: Date;
    public tokenSource: string;
    public env: string;
    public commercePlatformTosAgreedDate?: Date;
    public devCenterTosAgreedDate?: Date;
    public loggedIn: boolean = false;
    public name: {
        first: string;
        last: string;
    };

    public company?: Company;
    public companies: Array<Company> = [];
    public preferences: IUserPreferences = {
        language: [Cookie.get(LANGUAGE_COOKIE) || 'en-US'],
        locale: 'en-US',
        timezone: '',
        datetime: {}
    };

    private loginHandler = new EventHandler();
    private logoutHandler = new EventHandler();
    private prefsHandler = new EventHandler();

    /**
     * Set the user details from an object - as fetched from Identity.
     *
     * @param {Object} obj
     */
    public setDetails = (obj: any) => {
        this.id = obj.id;
        this.type = obj.user_type;
        this.title = obj.job_title;
        this.email = obj.email;
        this.roles = obj.roles;
        this.commercePlatformTosAgreedDate = obj.commerce_platform_tos_agreed_at ? new Date(obj.commerce_platform_tos_agreed_at) : null;
        this.devCenterTosAgreedDate = obj.developer_center_tos_agreed_at ? new Date(obj.developer_center_tos_agreed_at) : null;
        this.name = {
            first: obj.first_name,
            last: obj.last_name
        };
        this.companies = [];
        this.profile = new CurrentUserProfile(obj);
        this.company = (obj.organization) ? new Company(obj.organization) : null;
        if (obj.organizations) {
            this.companies = [];
            obj.organizations.forEach((company: Company) => {
                this.companies.push(new Company(company));
            });
        }
        this.setPreferences(obj.preferences);
    };

    /**
     * Set the user preferences from an object.
     *
     * @param prefs
     */
    public setPreferences = (prefs: object) => {
        let trigger = false;
        if (!prefs) { return; }
        Object.keys(prefs).forEach((key) => {
            if (prefs[key]) {
                if (key === 'language') {
                    if (typeof prefs[key] === 'string' && prefs[key].length > 0) {
                        prefs[key] = [prefs[key]];
                    } else if (Array.isArray(prefs[key]) && prefs[key].length === 0) {
                        prefs[key] = ['en-US'];
                    }

                    this.setLanguageCookie(prefs[key]);
                    accessibility.updateHtmlLanguage(prefs[key][0]);
                }
                this.preferences[key] = prefs[key];
                trigger = true;
            }
        });
        if (trigger) {
            this.prefsHandler.trigger(this.preferences);
        }
    };

    /**
     * Register a callback function that is fired when the user preferences change.
     *
     * @param {Function} fn
     * @param {Boolean} immediate, fire immediately
     * @returns {Function} unregister when called
     */
    public onPrefsChange = (fn, immediate: boolean = false) => {
        if (immediate) { fn(this.preferences); }
        return this.prefsHandler.register(fn);
    };

    /**
     * Register a callback function that is fired when the user becomes authorized.
     *
     * @param {Function} fn
     * @param {Boolean} immediate, fire immediately
     * @returns {Function} unregister when called
     */
    public onLogin = (fn, immediate: boolean = false) => {
        if (immediate && this.loggedIn) { fn(); }
        return this.loginHandler.register(fn);
    };

    /**
     * Register a callback function that is fired when the user becomes de-authorized.
     *
     * @param {Function} fn
     * @param {Boolean} immediate, fire immediately
     * @returns {Function} unregister when called
     */
    public onLogout = (fn, immediate: boolean = false) => {
        if (immediate && !this.loggedIn) { fn(); }
        return this.logoutHandler.register(fn);
    };

    /**
     * Quick check to see if there's evidence that the user has a session.
     *
     * @returns {Boolean}
     */
    public hasSession = () => {
        return (this.token && this.loggedIn);
    };

    /**
     * Attempt to create a user session by pulling the token from the browser,
     * validating it against Identity, populating user details, and fetching
     * the user's apps.
     *
     * @returns {Promise} resolves if session is started, rejects if not.
     */
    public startSession = ({hardcodedLaunchpad, tokenSource = "identity", token, env}) => {

        if (this.session) {
            return this.session;
        }

        this.tokenSource = tokenSource;
        this.env = env;

        $Router.setEnvironment(env);

        const effectiveToken = Cookie.get(TOKEN_COOKIE) ?? token;
        if (!effectiveToken) {
            return Promise.reject('notoken');
        }

        this.token = effectiveToken;
        IdentityService.setToken(effectiveToken);

        this.session = IdentityService.getMe().then((details) => {
            this.setDetails(details);
            this.saveUserPrefs(details);
            const appsPromise = this.getApps();
            const orgPromise = this.getOrg(details.organization.id);
            return Promise.all([appsPromise, orgPromise]).then(([appResp, orgResp]) => {
                // We always want the hardcoded launchpad app first
                // followed by the rest of apps
                this.apps = [
                    hardcodedLaunchpad,
                    ...appResp
                ]
                .filter(Boolean)
                .map(coerceAppLogoToObject);
                LodeRunnerService.checkLode(orgResp);
                this.loggedIn = true;
                this.loginHandler.trigger();
            });
        }).catch((err) => {
            if (err.status === 403) {
                this.destroySession(true);
            } else {
                /* tslint:disable-next-line:no-console */
                console.error(err);
            }
        });

        return this.session;
    };

    /**
     * Destroy the user session by removing the token cookie and cleaning up.
     * If silent param is true, logoutHandler will not be triggered.
     *
     * @param {Boolean} [silent]
     */
    public destroySession = (silent = false) => {
        if (this.token && this.tokenSource === "identity") {
            IdentityService.logout();
        }
        this.apps = [];
        this.token = null;
        this.session = null;
        this.setDetails({});
        this.loggedIn = false;
        Cookie.erase(TOKEN_COOKIE);
        window['Intercom']('shutdown'); // for some reason window.Intercom causes an error
        if (!silent) {
            this.logoutHandler.trigger();
        }
    };

    /**
     * Set a token in memory and in the browser's cookie stash.
     *
     * @param token
     */
    public setToken = (token: string) => {
        IdentityService.setToken(token);
        Cookie.set(TOKEN_COOKIE, token, {secure: true});
        this.token = token;
    };

    /**
     * Save the user's language preference in a cookie so it can be used
     * when the session is lost.
     *
     * @param {string|Array} language
     */
    public setLanguageCookie = (language: string|Array<string> = ['en-US']) => {
        const cookie: string = (Array.isArray(language)) ? language[0] as string : language as string;
        Cookie.erase(LANGUAGE_COOKIE);
        Cookie.set(LANGUAGE_COOKIE, cookie);
    };

    /**
     * Fetch the user's applications.
     *
     * @returns {Promise}
     */
    public getApps(): Promise<Array<any>> {
        return IdentityService.getSubscribedApps().then((apps) => {
            // we can return this since we shouldn't need to filter the response
            return ApplicationService.getAuthorizedApps(apps, this.roles);
        });
    }

    /**
     * Fetch the user's current organization data.
     *
     * @returns {Promise}
     */
    public getOrg(orgId): Promise<Array<any>> {
        return IdentityService.getCurrentOrg(orgId);
    }

    public saveUserPrefs(user) {
        let updateLanguagesRequired = false;

        // check to make sure user's language prefs are supported languages
        // and filter those that are not supported
        if (!this.languagesSupported(user.preferences.language)) {
            user.preferences.language = this.updateLanguagePrefs(user.preferences.language);
            updateLanguagesRequired = true;
        }

        if (this.checkSettingSpecificPrefs(user.preferences)) {
            const browserDetectedPrefs = prefHelpers.checkBrowserLocaleTimezone();
            Object.keys(browserDetectedPrefs).forEach((key, index) => {
                if (typeof browserDetectedPrefs[key] === 'string') {
                    user.preferences[key] = user.preferences[key] || browserDetectedPrefs[key];
                }
                if (typeof browserDetectedPrefs[key] === 'object' && this.isEmpty(user.preferences[key])) {
                    user.preferences[key] = browserDetectedPrefs[key];
                }
            });
            IdentityService.updateMe(user);

        // if all user prefs are set but the users language prefs
        // are not all supported, we need to update the language prefs
        } else if (updateLanguagesRequired) {
            IdentityService.updateMe(user);
        }
    }

    public hasCorrectOrgToken(): boolean {
        // Determines if the url contains an org_id query param
        const currentUrl = Url.getCurrent();
        const currentUrlObj = Url.parse(currentUrl);
        const searchObj = Url.parseSearch(currentUrlObj.search);
        const orgId = searchObj['cp.orgid'];

        if(orgId && this.company && this.company.id){
            const orgUserId = this.company.id;
            if(orgId !== orgUserId){
                return false;
            }
        }

        return this.hasSession();
    }

    public updateCommercePlatformTos() {
        return IdentityService.updateCommercePlatformTos(this.id);
    }

    public tosIsNotValid() {
        if(!this.commercePlatformTosAgreedDate) {
            return true;
        }
        const userTosTime = new Date(this.commercePlatformTosAgreedDate).getTime();
        const lastTosDate = new Date(SETTINGS.TOS_VALID_DATE.commercePlatform).getTime();
        return userTosTime < lastTosDate;
    }

    // verify that all users preferred languages are supported
    private languagesSupported = (languages: Array<string> = []): boolean => {
        return languages.every((lang) => {
            return SETTINGS.LOCALIZATION_OPTIONS.languages.hasOwnProperty(lang);
        });
    }

    // filter out languages that are not supported
    private updateLanguagePrefs = (languages: Array<string>): Array<string>  => {
        let langArr = languages.filter((lang) => {
            return SETTINGS.LOCALIZATION_OPTIONS.languages.hasOwnProperty(lang);
        });

        if (langArr.length < 1) {
            langArr = ['en-US'];
            return langArr;
        } else {
            return langArr;
        }
    }

    private checkSettingSpecificPrefs = (prefs: IUserPreferences) => {
        if (!prefs.locale) { return true; }
        if (!prefs.language) { return true; }
        if (!prefs.timezone) { return true; }
        if (!prefs.datetime || this.isEmpty(prefs.datetime)) { return true; }
        return false;
    };

    // TODO: move this to a general utility
    private isEmpty = (o) => {
        for (const name in o) {
            if (o.hasOwnProperty(name)) { return false; }
        }
        return true;
    };

}

export let $CurrentUser = Singleton('$CurrentUser', CurrentUser);

function coerceAppLogoToObject (app) {
    // Some older formats of logo are simple strings so we do some work here to coerce the
    // data to be an object. 
    if(!app.logo) {
        return {
            ...app,
            logo: {
                type: 'image',
                name: 'generic'
            }
        }
    } else if(typeof app.logo === "string") {
        // but we cannot know which logo strings are simple strings and which are json
        // without just trying to parse it :(
        try {
            const parsedLogo = JSON.parse(app.logo);

            // So we treat any successfully json parsed string like an object
            return {
                ...app,
                logo: parsedLogo
            }
        } catch(e) {
            // And then treating failures as simple strings and do our best to get
            // them into an object that makes sense
            return {
                ...app,
                logo: {
                    type: 'image',
                    name: app.logo || 'generic'
                }
            }
        }
        
    } else {
        // In the case it is already an object then we're happy
        return app;
    }
}